/**
 * Authoritative 64-bit vector type.
 *
 * IMPORTANT ARCHITECTURAL RULE
 * ---------------------------
 * `Vec3` — not `THREE.Vector3` — is the authoritative position type for
 * simulation, gravity, orbits and reference frames. JavaScript numbers are
 * IEEE-754 binary64, giving a 53-bit mantissa. Across a single solar system
 * (|r| <= ~1e13 m) the worst-case representable step is
 *
 *     1e13 / 2^53 ~= 1.1e-3 m
 *
 * i.e. about a millimetre, which is far below anything gameplay cares about.
 * That is why this project does not need integer-sector splitting for the base
 * solar system. `Sector.ts` exists for the future multi-system case and is used
 * by the serialization layer, not by the hot path.
 *
 * `THREE.Vector3` is binary32 (24-bit mantissa) and is only ever allowed to
 * hold *camera-relative* render coordinates, which are kept small by the
 * floating-origin and camera-relative rendering systems.
 */
export class Vec3 {
  x: number;
  y: number;
  z: number;

  constructor(x = 0, y = 0, z = 0) {
    this.x = x;
    this.y = y;
    this.z = z;
  }

  static zero(): Vec3 {
    return new Vec3(0, 0, 0);
  }

  static of(x: number, y: number, z: number): Vec3 {
    return new Vec3(x, y, z);
  }

  static fromArray(a: ArrayLike<number>, offset = 0): Vec3 {
    return new Vec3(a[offset], a[offset + 1], a[offset + 2]);
  }

  clone(): Vec3 {
    return new Vec3(this.x, this.y, this.z);
  }

  set(x: number, y: number, z: number): this {
    this.x = x;
    this.y = y;
    this.z = z;
    return this;
  }

  copy(v: Vec3): this {
    this.x = v.x;
    this.y = v.y;
    this.z = v.z;
    return this;
  }

  setScalar(s: number): this {
    this.x = s;
    this.y = s;
    this.z = s;
    return this;
  }

  add(v: Vec3): this {
    this.x += v.x;
    this.y += v.y;
    this.z += v.z;
    return this;
  }

  addScaled(v: Vec3, s: number): this {
    this.x += v.x * s;
    this.y += v.y * s;
    this.z += v.z * s;
    return this;
  }

  sub(v: Vec3): this {
    this.x -= v.x;
    this.y -= v.y;
    this.z -= v.z;
    return this;
  }

  mulScalar(s: number): this {
    this.x *= s;
    this.y *= s;
    this.z *= s;
    return this;
  }

  negate(): this {
    this.x = -this.x;
    this.y = -this.y;
    this.z = -this.z;
    return this;
  }

  dot(v: Vec3): number {
    return this.x * v.x + this.y * v.y + this.z * v.z;
  }

  cross(v: Vec3): this {
    const { x, y, z } = this;
    this.x = y * v.z - z * v.y;
    this.y = z * v.x - x * v.z;
    this.z = x * v.y - y * v.x;
    return this;
  }

  lengthSq(): number {
    return this.x * this.x + this.y * this.y + this.z * this.z;
  }

  length(): number {
    return Math.hypot(this.x, this.y, this.z);
  }

  distanceTo(v: Vec3): number {
    return Math.hypot(this.x - v.x, this.y - v.y, this.z - v.z);
  }

  distanceToSq(v: Vec3): number {
    const dx = this.x - v.x;
    const dy = this.y - v.y;
    const dz = this.z - v.z;
    return dx * dx + dy * dy + dz * dz;
  }

  normalize(): this {
    const len = this.length();
    if (len > 0) this.mulScalar(1 / len);
    return this;
  }

  /** Returns a unit-length copy; a zero vector stays zero. */
  normalized(): Vec3 {
    return this.clone().normalize();
  }

  /** Shortens the vector to at most `max`, leaving its direction alone. */
  clampLength(max: number): this {
    const len = this.length();
    if (len > max && len > 0) this.mulScalar(max / len);
    return this;
  }

  lerp(v: Vec3, t: number): this {
    this.x += (v.x - this.x) * t;
    this.y += (v.y - this.y) * t;
    this.z += (v.z - this.z) * t;
    return this;
  }

  /** Component of `this` parallel to unit vector `n`. */
  projectOnUnit(n: Vec3): Vec3 {
    return n.clone().mulScalar(this.dot(n));
  }

  /** Component of `this` perpendicular to unit vector `n`. */
  rejectFromUnit(n: Vec3): Vec3 {
    return this.clone().sub(this.projectOnUnit(n));
  }

  isFinite(): boolean {
    return Number.isFinite(this.x) && Number.isFinite(this.y) && Number.isFinite(this.z);
  }

  equalsApprox(v: Vec3, eps = 1e-9): boolean {
    return Math.abs(this.x - v.x) <= eps && Math.abs(this.y - v.y) <= eps && Math.abs(this.z - v.z) <= eps;
  }

  toArray(): [number, number, number] {
    return [this.x, this.y, this.z];
  }

  toString(): string {
    return `(${this.x.toPrecision(8)}, ${this.y.toPrecision(8)}, ${this.z.toPrecision(8)})`;
  }

  // ---- Allocation-free static helpers -------------------------------------

  static addVectors(a: Vec3, b: Vec3, out = new Vec3()): Vec3 {
    out.x = a.x + b.x;
    out.y = a.y + b.y;
    out.z = a.z + b.z;
    return out;
  }

  static subVectors(a: Vec3, b: Vec3, out = new Vec3()): Vec3 {
    out.x = a.x - b.x;
    out.y = a.y - b.y;
    out.z = a.z - b.z;
    return out;
  }

  static crossVectors(a: Vec3, b: Vec3, out = new Vec3()): Vec3 {
    const x = a.y * b.z - a.z * b.y;
    const y = a.z * b.x - a.x * b.z;
    const z = a.x * b.y - a.y * b.x;
    out.x = x;
    out.y = y;
    out.z = z;
    return out;
  }

  static scale(a: Vec3, s: number, out = new Vec3()): Vec3 {
    out.x = a.x * s;
    out.y = a.y * s;
    out.z = a.z * s;
    return out;
  }

  /** Any unit vector perpendicular to `n` (numerically stable). */
  static anyPerpendicular(n: Vec3): Vec3 {
    const a = Math.abs(n.x) < 0.9 ? new Vec3(1, 0, 0) : new Vec3(0, 1, 0);
    return a.cross(n).normalize();
  }

  /** Angle between two vectors in radians, clamped against rounding error. */
  static angleBetween(a: Vec3, b: Vec3): number {
    const denom = a.length() * b.length();
    if (denom === 0) return 0;
    const c = Math.min(1, Math.max(-1, a.dot(b) / denom));
    return Math.acos(c);
  }
}
